import React, { useState } from 'react';
import { formatDisplayEmail } from '../../utils';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Checkbox,
  Avatar,
  Group,
  Text,
  Badge,
  Menu,
  ActionIcon,
  ScrollArea,
  Paper,
  Stack,
  Tooltip,
  Pagination,
  Combobox,
  InputBase,
  NumberInput,
  useCombobox,
} from '@mantine/core';
import { IconDotsVertical, IconEdit, IconSettings, IconPower, IconKey, IconRotateClockwise } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';

// 分页大小预设项
const PAGE_SIZE_PRESETS = [20, 50, 100, 200];
// 自定义分页大小的上限
const MAX_PAGE_SIZE = 1000;

// 分页大小选择器：保留预设项，同时支持输入自定义数字
interface PageSizeSelectProps {
  value: number;
  onChange: (size: number) => void;
}

const PageSizeSelect: React.FC<PageSizeSelectProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const [custom, setCustom] = useState<number | string>('');

  const submit = (size: number) => {
    if (!Number.isFinite(size) || size <= 0) return;
    const clamped = Math.min(Math.max(1, Math.floor(size)), MAX_PAGE_SIZE);
    onChange(clamped);
    setCustom('');
    combobox.closeDropdown();
  };

  return (
    <Combobox store={combobox} width={150} position="bottom-end" onOptionSubmit={(val) => submit(Number(val))}>
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          size="xs"
          w={90}
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
        >
          {value}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {PAGE_SIZE_PRESETS.map((p) => (
            <Combobox.Option value={String(p)} key={p} active={p === value}>
              {p}
            </Combobox.Option>
          ))}
        </Combobox.Options>
        <div style={{ padding: 8, borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          <NumberInput
            size="xs"
            label={t('adminConsole.newConsole.table.custom')}
            placeholder={`1-${MAX_PAGE_SIZE}`}
            min={1}
            max={MAX_PAGE_SIZE}
            clampBehavior="strict"
            hideControls
            value={custom}
            onChange={setCustom}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit(Number(custom));
              }
            }}
          />
        </div>
      </Combobox.Dropdown>
    </Combobox>
  );
};

// 权限颜色映射
const getPermissionColor = (level: number): string => {
  switch (level) {
    case 1:
      return 'red'; // 公司所有者 - 红色
    case 2:
      return 'orange'; // 公司管理员 - 橙色
    case 3:
      return 'yellow'; // 部门管理员 - 黄色
    case 4:
      return 'blue'; // 编辑者 - 蓝色
    case 5:
      return 'green'; // 查看者 - 绿色
    default:
      return 'gray';
  }
};

// 获取权限文本翻译
const getPermissionText = (roleCode: string, t: any) => {
  switch (roleCode) {
    case 'ROLE_COMPANY_OWNER':
      return t('ROLE_COMPANY_OWNER') || 'Company Owner';
    case 'ROLE_ADMIN':
      return t('ROLE_ADMIN') || 'Administrator';
    case 'ROLE_ADMIN_DEPARTMENT':
      return t('ROLE_ADMIN_DEPARTMENT') || 'Department Admin';
    case 'ROLE_EDITOR':
      return t('ROLE_EDITOR') || 'Editor';
    case 'ROLE_VIEWER':
      return t('ROLE_VIEWER') || 'Viewer';
    default:
      return t('ROLE_VIEWER') || 'Viewer';
  }
};

// 获取状态文本翻译
const getStatusText = (enabled: boolean, t: any) => {
  return enabled
    ? t('adminConsole.newConsole.member.status.enabled') || 'Enabled'
    : t('adminConsole.newConsole.member.status.disabled') || 'Disabled';
};

// 翻译部门路径中的未分配文本
const translateDepartmentPath = (departmentPath: string, t: any) => {
  if (departmentPath === '__UNASSIGNED__') {
    return t('adminConsole.newConsole.unassigned') || 'Unassigned';
  }
  // 替换路径中的 __UNASSIGNED__ 标识符
  return departmentPath.replace(/__UNASSIGNED__/g, t('adminConsole.newConsole.unassigned') || 'Unassigned');
};

interface Member {
  id: number | string;
  uuid?: string; // 用户UUID
  name: string;
  email: string;
  avatar: string;
  department: string;
  departmentPath: string;
  permissionLevel: number;
  permissionText: string;
  roleCode: string; // 添加roleCode用于实时翻译
  status: string;
  updateAt: string;
  apiKeyBound: boolean;
}

interface MemberListProps {
  members: Member[];
  selectedMembers: string[];
  activePage: number;
  totalPage: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onToggleAll: () => void;
  onToggleSelection: (memberId: number | string) => void;
  onEdit: (member: Member) => void;
  onStatusSetting: (member: Member) => void;
  onApiBinding: (member: Member) => void;
  onDeptPermissionSetting: (member: Member) => void;
  onPasswordReset: (member: Member) => void;
  onDepartmentClick?: (departmentName: string, fullPath: string) => void;
  canEditUser?: (member: Member) => boolean;
  canDeleteUser: (member: Member) => boolean;
  canSetUserStatus?: (member: Member) => boolean;
  canManageApiKeys?: (member: Member) => boolean;
  canAssignUserToDepartment?: (member: Member) => boolean;
  canResetPassword?: (member: Member) => boolean;
  onPageChange: (page: number) => void;
}

export const MemberList: React.FC<MemberListProps> = ({
  members,
  selectedMembers,
  activePage,
  totalPage,
  pageSize,
  onPageSizeChange,
  onToggleAll,
  onToggleSelection,
  onEdit,
  onStatusSetting,
  onApiBinding,
  onDeptPermissionSetting,
  onPasswordReset,
  onDepartmentClick,
  canEditUser,
  canSetUserStatus,
  canManageApiKeys,
  canAssignUserToDepartment,
  canResetPassword,
  canDeleteUser,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const matches = useMediaQuery('(max-height: 600px)');
  const isCloudMode = import.meta.env.VITE_MODE !== 'local';

  console.log('members', members);

  const rows = members.map((member) => (
    <Table.Tr key={member.id} style={{ cursor: 'default' }}>
      <Table.Td>
        <Checkbox
          checked={selectedMembers.includes(member.id.toString())}
          disabled={!canDeleteUser(member)}
          onChange={() => onToggleSelection(member.id)}
        />
      </Table.Td>

      <Table.Td>
        <Group gap="sm">
          <Avatar size="sm" color="blue">
            {member.avatar}
          </Avatar>
          <Stack w={140}>
            <Tooltip label={member.name} position="top-start">
              <Text size="sm" fw={500} truncate="end">
                {member.name}
              </Text>
            </Tooltip>
            <Tooltip label={formatDisplayEmail(member.email)} position="top-start">
              <Text size="xs" c="dimmed">
                {formatDisplayEmail(member.email)}
              </Text>
            </Tooltip>
          </Stack>
        </Group>
      </Table.Td>

      <Table.Td>
        <Text size="sm" c="dimmed">
          {(() => {
            // 先翻译整个路径，然后再split处理
            const translatedPath = translateDepartmentPath(member.departmentPath, t);
            const parts = translatedPath.split(' > ');

            // 公司管理员，直接展示特定符号
            if (member.roleCode === 'ROLE_COMPANY_OWNER') {
              return '-';
            }

            return parts.map((part, index, array) => {
              // 构建从根到当前部门的完整路径（使用原始路径）
              const originalParts = member.departmentPath.split(' > ');
              const fullPath = originalParts.slice(0, index + 1).join(' > ');

              return (
                <span key={index}>
                  <Text
                    component="span"
                    size="sm"
                    c="blue"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                    onClick={() => {
                      if (onDepartmentClick) {
                        // 传递原始部门名称和完整路径用于逻辑处理
                        const originalPart = originalParts[index];
                        onDepartmentClick(originalPart, fullPath);
                      }
                    }}
                  >
                    {part}
                  </Text>
                  {index < array.length - 1 && (
                    <Text component="span" size="sm" c="dimmed">
                      {' '}
                      &gt;{' '}
                    </Text>
                  )}
                </span>
              );
            });
          })()}
        </Text>
      </Table.Td>

      <Table.Td>
        <Tooltip label={getPermissionText(member.roleCode, t)} position="top" withArrow>
          <Badge size="sm" color={getPermissionColor(member.permissionLevel)}>
            {getPermissionText(member.roleCode, t)}
          </Badge>
        </Tooltip>
      </Table.Td>

      <Table.Td>
        <Tooltip label={getStatusText(member.enabled, t)} position="top" withArrow>
          <Badge size="sm" variant="filled" color={member.enabled ? 'green' : 'orange'}>
            {getStatusText(member.enabled, t)}
          </Badge>
        </Tooltip>
      </Table.Td>

      <Table.Td>
        <Text size="sm" c="dimmed">
          {member.updateAt}
        </Text>
      </Table.Td>

      <Table.Td>
        {(() => {
          // 检查是否有任何操作权限 - 只有当用户有实际权限时才显示操作栏
          // Cloud 模式下编辑、API Key、重置密码不可用
          const hasEditPermission = !isCloudMode && (!canEditUser || canEditUser(member));
          const hasAssignPermission = !canAssignUserToDepartment || canAssignUserToDepartment(member);
          const hasStatusPermission = !canSetUserStatus || canSetUserStatus(member);
          const hasApiKeysPermission = !isCloudMode && (!canManageApiKeys || canManageApiKeys(member));
          const hasPasswordPermission = !isCloudMode && (!canResetPassword || canResetPassword(member));

          // 如果所有可用操作都没有权限，则隐藏操作栏
          if (
            !hasEditPermission &&
            !hasAssignPermission &&
            !hasStatusPermission &&
            !hasApiKeysPermission &&
            !hasPasswordPermission
          ) {
            return null;
          }

          return (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray">
                  <IconDotsVertical size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {!isCloudMode && (!canEditUser || canEditUser(member)) && (
                  <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onEdit(member)}>
                    {t('adminConsole.newConsole.menu.edit')}
                  </Menu.Item>
                )}
                {(!canAssignUserToDepartment || canAssignUserToDepartment(member)) && (
                  <Menu.Item leftSection={<IconSettings size={14} />} onClick={() => onDeptPermissionSetting(member)}>
                    {t('adminConsole.newConsole.menu.deptPermission')}
                  </Menu.Item>
                )}
                {(!canSetUserStatus || canSetUserStatus(member)) && (
                  <Menu.Item leftSection={<IconPower size={14} />} onClick={() => onStatusSetting(member)}>
                    {t('adminConsole.newConsole.menu.statusSetting')}
                  </Menu.Item>
                )}
                {!isCloudMode && (!canManageApiKeys || canManageApiKeys(member)) && (
                  <Menu.Item leftSection={<IconKey size={14} />} onClick={() => onApiBinding(member)}>
                    {t('adminConsole.newConsole.menu.apiBinding')}
                  </Menu.Item>
                )}
                {!isCloudMode && (!canResetPassword || canResetPassword(member)) && (
                  <Menu.Item leftSection={<IconRotateClockwise size={14} />} onClick={() => onPasswordReset(member)}>
                    {t('adminConsole.newConsole.menu.passwordReset')}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          );
        })()}
      </Table.Td>
    </Table.Tr>
  ));

  if (members.length === 0) {
    return (
      <Paper p="xl" radius="md" style={{ textAlign: 'center' }}>
        <Text c="dimmed">{t('adminConsole.newConsole.table.noData')}</Text>
      </Paper>
    );
  }

  const allSelectChecked = selectedMembers.length === members.length && members.length > 0;
  const indeterminate = selectedMembers.length > 0 && selectedMembers.length < members.length;
  const hasSelectableUser = members.some((member) => canDeleteUser(member));

  return (
    <div style={{ overflow: 'auto', flex: 1 }}>
      <Paper radius="md">
        <ScrollArea h={matches ? 'calc(100vh - 315px)' : 'calc(100vh - 270px)'} type="never">
          <Table striped highlightOnHover layout="fixed">
            <Table.Thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>
                  <Checkbox
                    checked={allSelectChecked}
                    indeterminate={indeterminate}
                    disabled={!hasSelectableUser}
                    onChange={onToggleAll}
                  />
                </Table.Th>
                <Table.Th w={180}>{t('adminConsole.newConsole.table.userInfo')}</Table.Th>
                <Table.Th w={150}>{t('adminConsole.newConsole.table.departmentPath')}</Table.Th>
                <Table.Th w={100}>{t('adminConsole.newConsole.table.permissionLevel')}</Table.Th>
                <Table.Th w={100}>{t('adminConsole.newConsole.table.status')}</Table.Th>
                <Table.Th w={120}>{t('adminConsole.newConsole.table.updateAt')}</Table.Th>
                <Table.Th style={{ width: 60 }}>{t('adminConsole.newConsole.table.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>
      <div className="w-full flex justify-center items-center gap-4 mt-4">
        <Pagination value={activePage} onChange={onPageChange} total={totalPage} />
        <Group gap="xs" align="center">
          <Text size="sm" c="dimmed">
            {t('adminConsole.newConsole.table.perPage')}
          </Text>
          <PageSizeSelect value={pageSize} onChange={onPageSizeChange} />
        </Group>
      </div>
    </div>
  );
};
